import { Medal, UserCheck, Clock, SlidersHorizontal } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";

const missionVision = [
  {
    tag: "Our Mission",
    text: "To deliver superior security and housekeeping solutions that create safe, secure, and hygienic environments for our clients, allowing them to focus on their core activities without worry.",
  },
  {
    tag: "Our Vision",
    text: "To be recognized as the gold standard in the security industry, known for our integrity, rapid response, and the high caliber of our personnel.",
  },
];

const whyReasons = [
  {
    icon: Medal,
    title: "Founded by a Veteran",
    text: "Leadership by an Ex-Serviceman guarantees a hierarchy based on discipline and accountability.",
  },
  {
    icon: UserCheck,
    title: "Rigorous Training",
    text: "Our staff undergoes strict background checks and physical training before deployment.",
  },
  {
    icon: Clock,
    title: "24/7 Support",
    text: "Security never sleeps, and neither do we. We offer round-the-clock supervision and support.",
  },
  {
    icon: SlidersHorizontal,
    title: "Customized Solutions",
    text: "We understand that a hospital has different security needs than a construction site. We tailor our plans to fit your specific risks.",
  },
];

export default function About() {
  return (
    <>
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <Eyebrow>About STARLINK Security Force</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-slate-900" data-testid="about-headline">
            <MaskedLine delay={0.05}>Guarding with Valor,</MaskedLine>
            <MaskedLine delay={0.17} className="italic text-[#0F2A4A]">Serving with Integrity.</MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-600">
              Welcome to STARLINK Security Force, where military precision meets modern security
              solutions. Established in 2023, we have quickly risen to become a trusted partner for
              businesses, residential complexes, and individuals seeking uncompromised safety and
              pristine facility management.
            </p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
              In an uncertain world, the need for reliable protection is paramount. We do not just
              provide manpower; we provide peace of mind. Our approach is rooted in discipline,
              rigorous training, and a deep understanding of threat management.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20" data-testid="about-mission-vision">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-2 gap-6">
          {missionVision.map((m, i) => (
            <Reveal key={m.tag} delay={i * 0.12} className="h-full">
              <div
                data-testid={i === 0 ? "about-mission-card" : "about-vision-card"}
                className="noise-overlay relative h-full overflow-hidden rounded-2xl bg-[#0F2A4A] p-9"
              >
                <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#E5322D]/15 blur-3xl" />
                <span className="relative font-mono text-sm sm:text-base font-bold tracking-[0.22em] uppercase text-red-400 underline underline-offset-4 decoration-red-400/70">
                  {m.tag}
                </span>
                <p className="relative mt-4 text-base leading-relaxed text-slate-200">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>Our Leadership</Eyebrow>
              <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900" data-testid="leadership-heading">
                The Military Edge
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-600">
                The backbone of STARLINK Security Force is our founder, Mr. Ashok Kumar. As an
                Ex-Army serviceman, Mr. Kumar founded this company with a singular vision: to bring
                the ethos of the Indian Armed Forces—discipline, duty, and vigilance—into the
                private security sector.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                Unlike standard agencies, STARLINK operates on the principles of military-grade
                strategy. Mr. Kumar's experience in high-stakes environments ensures that every
                guard we deploy is not just a watchman, but a trained professional capable of
                assessing risks, maintaining order, and reacting swiftly to emergencies.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="noise-overlay relative overflow-hidden rounded-2xl bg-[#0F2A4A] p-9" data-testid="founder-quote-card">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#E5322D]/15 blur-3xl" />
              <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white/10 font-display text-2xl text-white">
                AK
              </span>
              <p className="relative mt-6 font-serif text-xl italic leading-relaxed text-white">
                "Security is not a job; it is a duty. At STARLINK, we carry the legacy of service
                in every assignment we undertake."
              </p>
              <p className="relative mt-5 font-mono text-[11px] tracking-[0.2em] uppercase text-red-400">
                Ashok Kumar — Founder
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="mb-14 text-center max-w-3xl mx-auto">
            <h2 className="font-sans text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="about-why-choose-heading">
              Why Choose <span className="text-[#E5322D]">STARLINK Security Force</span>?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              At STARLINK Security Force, we are committed to excellence. When you hire us, you
              aren't just hiring a service provider; you are partnering with a force dedicated to
              your protection.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyReasons.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.1} className="h-full">
                <div
                  className="h-full rounded-2xl border border-slate-100 bg-white px-7 py-10 text-center shadow-[0_18px_50px_-20px_rgba(15,23,42,0.12)] hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-20px_rgba(229,50,45,0.18)] transition-all duration-300"
                  data-testid={`about-why-card-${i}`}
                >
                  <r.icon className="mx-auto h-12 w-12 text-[#E5322D]" strokeWidth={1.25} />
                  <h3 className="mt-5 text-xl font-extrabold tracking-tight text-slate-900">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
